import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, IconButton, Switch, Chip, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Autocomplete,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Page, Card } from '../ui';

const WINDOWS = [
  { v: 'today', l: 'Сегодня' }, { v: 'yesterday', l: 'Вчера' }, { v: 'last_3d', l: '3 дня' },
  { v: 'last_7d', l: '7 дней' }, { v: 'last_14d', l: '14 дней' }, { v: 'last_30d', l: '30 дней' },
];
const ACTION_LABEL = { stop: 'Стоп', scale: 'Скейл', hard_stop: 'Хард-стоп', skip: 'Пропуск', alert: 'Алерт' };

const emptyFunnel = () => ({
  name: '', funnel_id: '', enabled: false, dry_run: true,
  config: {
    action_target: 'adset', window: 'today',
    limits: { cpc: '', cpm: '', cpl: '', strict_cpc: '' },
    hardstops: { spend_no_clicks: '', spend_no_leads: '', funnel_spend_no_leads: '' },
    min_spend: 0, min_clicks: 0, min_leads: 2,
    scaling: { enabled: false, lead_first: '', lead_extra: '', max_budget: '' },
    cooldown_minutes: 30, account_ids: [],
  },
});

function Num({ label, value, onChange, width = 130, adorn = '$' }) {
  return (
    <TextField size="small" type="number" label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      sx={{ width }} InputProps={{ startAdornment: adorn ? <Typography sx={{ mr: 0.5, color: 'text.secondary', fontSize: 13 }}>{adorn}</Typography> : null }} />
  );
}

function FunnelEditor({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState(emptyFunnel());
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    if (open) {
      const base = emptyFunnel();
      setF(initial ? { ...base, ...initial, config: { ...base.config, ...(initial.config || {}),
        limits: { ...base.config.limits, ...((initial.config || {}).limits || {}) },
        hardstops: { ...base.config.hardstops, ...((initial.config || {}).hardstops || {}) },
        scaling: { ...base.config.scaling, ...((initial.config || {}).scaling || {}) } } } : base);
    }
  }, [open, initial]);
  useEffect(() => { if (open) axios.get('/accounts').then((x) => setAccounts(x.data)).catch(() => {}); }, [open]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setC = (k, v) => setF((p) => ({ ...p, config: { ...p.config, [k]: v } }));
  const setGrp = (grp, k, v) => setF((p) => ({ ...p, config: { ...p.config, [grp]: { ...p.config[grp], [k]: v } } }));
  const c = f.config;

  const save = async () => {
    setSaving(true);
    try {
      if (f.id) await axios.put(`/funnels/${f.id}`, f);
      else await axios.post('/funnels', f);
      onSaved(); onClose();
    } catch (e) { /* ignore */ }
    setSaving(false);
  };

  const secSx = { fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 2, mb: 1 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{f.id ? 'Изменить воронку' : 'Новая воронка'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField label="Название" size="small" value={f.name} onChange={(e) => set('name', e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
          <TextField label="Funnel ID (метка ||ID:XXX)" size="small" value={f.funnel_id} onChange={(e) => set('funnel_id', e.target.value)} sx={{ width: 200 }} />
        </Box>
        <Alert severity="info" sx={{ mt: 1.5, borderRadius: '8px', fontSize: 12 }}>
          Воронка ловит кампании, в названии которых есть <b>||ID:{f.funnel_id || 'XXX'}</b> (или ||ID-XXX). Совпадает точный ID или начинающийся с «{f.funnel_id || 'XXX'}_».
        </Alert>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Уровень действий</InputLabel>
            <Select label="Уровень действий" value={c.action_target} onChange={(e) => setC('action_target', e.target.value)}>
              <MenuItem value="adset">Группы объявлений</MenuItem>
              <MenuItem value="campaign">Кампании</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Окно метрик</InputLabel>
            <Select label="Окно метрик" value={c.window} onChange={(e) => setC('window', e.target.value)}>
              {WINDOWS.map((w) => <MenuItem key={w.v} value={w.v}>{w.l}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Typography sx={secSx}>Лимиты (пусто = выкл)</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Num label="Max CPC" value={c.limits.cpc} onChange={(v) => setGrp('limits', 'cpc', v)} />
          <Num label="Strict CPC" value={c.limits.strict_cpc} onChange={(v) => setGrp('limits', 'strict_cpc', v)} />
          <Num label="Max CPM" value={c.limits.cpm} onChange={(v) => setGrp('limits', 'cpm', v)} />
          <Num label="Max CPL" value={c.limits.cpl} onChange={(v) => setGrp('limits', 'cpl', v)} />
        </Box>

        <Typography sx={secSx}>Хард-стопы (расход без событий)</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Num label="Расход без кликов" value={c.hardstops.spend_no_clicks} onChange={(v) => setGrp('hardstops', 'spend_no_clicks', v)} width={160} />
          <Num label="Расход без лидов" value={c.hardstops.spend_no_leads} onChange={(v) => setGrp('hardstops', 'spend_no_leads', v)} width={160} />
          <Num label="Воронка без лидов" value={c.hardstops.funnel_spend_no_leads} onChange={(v) => setGrp('hardstops', 'funnel_spend_no_leads', v)} width={170} />
        </Box>

        <Typography sx={secSx}>Гарды (мин. данные)</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Num label="Мин. расход" value={c.min_spend} onChange={(v) => setC('min_spend', v)} />
          <Num label="Мин. кликов" value={c.min_clicks} onChange={(v) => setC('min_clicks', v)} adorn="" width={120} />
          <Num label="Мин. лидов" value={c.min_leads} onChange={(v) => setC('min_leads', v)} adorn="" width={120} />
          <Num label="Cooldown, мин" value={c.cooldown_minutes} onChange={(v) => setC('cooldown_minutes', v)} adorn="" width={140} />
        </Box>

        <Typography sx={secSx}>Скейлинг бюджета (по лидам, только вверх)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Switch checked={!!c.scaling.enabled} onChange={(e) => setGrp('scaling', 'enabled', e.target.checked)} />
          <Typography sx={{ fontSize: 13 }}>Включить скейлинг</Typography>
        </Box>
        {c.scaling.enabled && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Num label="За 1-й лид" value={c.scaling.lead_first} onChange={(v) => setGrp('scaling', 'lead_first', v)} />
            <Num label="За каждый след." value={c.scaling.lead_extra} onChange={(v) => setGrp('scaling', 'lead_extra', v)} width={150} />
            <Num label="Потолок бюджета" value={c.scaling.max_budget} onChange={(v) => setGrp('scaling', 'max_budget', v)} width={160} />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Autocomplete
          multiple size="small" options={accounts} getOptionLabel={(o) => o.name || o.id}
          value={accounts.filter((a) => (c.account_ids || []).includes(a.id))}
          onChange={(e, v) => setC('account_ids', v.map((x) => x.id))}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => <TextField {...params} label="Аккаунты (пусто = все)" placeholder={(c.account_ids || []).length ? '' : 'все профили'} />}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={f.dry_run} onChange={(e) => set('dry_run', e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Dry Run (симуляция)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Включена</Typography>
          </Box>
        </Box>
        {!f.dry_run && (
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '6px' }}>
            Dry Run выключен — воронка будет реально останавливать и менять бюджеты в Facebook.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={saving || !f.name.trim() || !f.funnel_id.trim()}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FunnelsPage() {
  const [funnels, setFunnels] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    axios.get('/funnels').then((r) => setFunnels(r.data)).catch(() => {});
    axios.get('/funnels/logs').then((r) => setLogs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (fn, field) => {
    try { await axios.put(`/funnels/${fn.id}`, { ...fn, [field]: !fn[field] }); load(); } catch (e) { /* ignore */ }
  };
  const remove = async (id) => { try { await axios.delete(`/funnels/${id}`); load(); } catch (e) { /* ignore */ } };
  const run = async (id) => {
    setToast(null);
    try { const r = await axios.post(`/funnels/${id}/run`); setToast({ t: 'success', x: `Готово. Действий: ${r.data.acted}` }); load(); }
    catch (e) { setToast({ t: 'error', x: 'Ошибка запуска' }); }
  };
  const doPreview = async (id) => {
    try { const r = await axios.get(`/funnels/${id}/preview`); setPreview(r.data); } catch (e) { setPreview({ count: 0, matched: [], error: true }); }
  };

  return (
    <Page
      title="Воронки"
      subtitle="Автоматика по метке ||ID:XXX — лимиты, хард-стопы и скейлинг. По умолчанию Dry Run."
      action={<Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setEditing(null); setEditorOpen(true); }}>Новая воронка</Button>}
    >
      {toast && <Alert severity={toast.t} sx={{ mb: 2, borderRadius: '6px' }} onClose={() => setToast(null)}>{toast.x}</Alert>}

      {funnels.length === 0 ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>Воронок пока нет</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5 }}>Добавь метку ||ID:XXX в названия кампаний и создай воронку с этим ID.</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {funnels.map((fn) => (
            <Card key={fn.id} sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>{fn.name}</Typography>
                    <Chip size="small" label={`||ID:${fn.funnel_id}`} color="primary" variant="outlined" sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />
                    {fn.dry_run && <Chip size="small" label="Dry Run" color="warning" variant="outlined" sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }} />}
                    <Chip size="small" label={fn.config?.action_target === 'campaign' ? 'кампании' : 'адсеты'} variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    {[fn.config?.limits?.cpc && `CPC≤${fn.config.limits.cpc}`, fn.config?.limits?.cpl && `CPL≤${fn.config.limits.cpl}`,
                      fn.config?.limits?.cpm && `CPM≤${fn.config.limits.cpm}`, fn.config?.scaling?.enabled && 'скейлинг']
                      .filter(Boolean).join(' · ') || 'лимиты не заданы'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <Switch size="small" checked={fn.enabled} onChange={() => toggle(fn, 'enabled')} />
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{fn.enabled ? 'Вкл' : 'Выкл'}</Typography>
                  </Box>
                  <IconButton size="small" title="Превью" onClick={() => doPreview(fn.id)}><VisibilityRoundedIcon fontSize="small" /></IconButton>
                  <IconButton size="small" title="Запустить" onClick={() => run(fn.id)}><PlayArrowRoundedIcon fontSize="small" /></IconButton>
                  <IconButton size="small" title="Изменить" onClick={() => { setEditing(fn); setEditorOpen(true); }}><EditRoundedIcon fontSize="small" /></IconButton>
                  <IconButton size="small" title="Удалить" onClick={() => remove(fn.id)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mt: 4, mb: 1.5 }}>
        Журнал решений
      </Typography>
      <Card sx={{ p: 0, overflow: 'hidden' }}>
        {logs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>Пока пусто. Запусти воронку или дождись расписания.</Box>
        ) : (
          <Box>
            {logs.map((l, i) => (
              <Box key={l.id || i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25, borderBottom: i < logs.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', width: 130, flexShrink: 0 }}>
                  {l.evaluated_at ? new Date(l.evaluated_at).toLocaleString('ru-RU') : ''}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', width: 160, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.entity_name || '—'}</Typography>
                <Chip size="small" label={ACTION_LABEL[(l.action || '').replace(' (Dry Run)', '')] || l.action || '—'} variant="outlined"
                  color={l.action && l.action.startsWith('hard_stop') ? 'error' : l.action && l.action.startsWith('scale') ? 'success' : 'default'}
                  sx={{ height: 22, fontSize: 11 }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.detail}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Card>

      <FunnelEditor open={editorOpen} initial={editing} onClose={() => setEditorOpen(false)} onSaved={load} />

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Совпавшие сущности</DialogTitle>
        <DialogContent dividers>
          {preview && preview.count === 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Ничего не совпало. Проверь, что метка ||ID:XXX есть в названиях кампаний и совпадает ID.
            </Typography>
          )}
          {preview && preview.matched && preview.matched.map((e, i) => (
            <Box key={i} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{e.name}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{e.campaign ? `${e.campaign} · ` : ''}spend ${e.spend} · clicks {e.clicks} · leads {e.leads}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>Закрыть</Button></DialogActions>
      </Dialog>
    </Page>
  );
}
